import React, { useState, useContext, useMemo } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Container,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { LoadingModalContext } from "../../../../main-pages/ServicePage";
import MessagePopup from "../../../../popup-components/MessagePopup";
import CustomStepper from "../../../../custom-components/CustomStepper";
import {
  getStepperSteps,
  getStepIndex,
  getNextStep,
  getPreviousStep,
} from "../../../../../utils/stepperConfig";
import { playwrightTypeScriptProjectAPI, updateGitHubProjectPlaywrightTypeScript } from "../../../../../services/webServices";
import PlaywrightTypeScriptSuccessPopup from "../../../../output-components/test-automation/PlaywrightTypeScriptSuccessPopup";
import GitHubConfigDrawer from "../../../../output-components/test-automation/GitHubConfigDrawer";
import GitHubSuccessDrawer from "../../../../output-components/test-automation/GitHubSuccessDrawer";
import { downloadPlaywrightTypeScriptProject } from "../../../../download/playwrightTypeScriptProjectDownload";

// Utility functions to handle JSON array format for test files
const convertTestFilesArrayToString = (testFilesArray) => {
  if (!Array.isArray(testFilesArray)) {
    return "// No test files generated";
  }

  if (testFilesArray.length === 0) {
    return "// No test files generated";
  }

  return testFilesArray
    .map((fileObj) => {
      const filePath = Object.keys(fileObj)[0];
      const content = fileObj[filePath];
      return `// ${filePath}\n${content}\n`;
    })
    .join("\n");
};

const convertTestFilesStringToArray = (testFilesString) => {
  if (!testFilesString || typeof testFilesString !== "string") {
    return [];
  }

  const fileSections = testFilesString.split(/\/\/ tests\/specs\/[^\n]+/);
  const fileComments =
    testFilesString.match(/\/\/ tests\/specs\/[^\n]+/g) || [];

  const result = [];
  for (let i = 0; i < fileComments.length; i++) {
    const filePath = fileComments[i].replace("// ", "");
    const content = fileSections[i + 1]?.trim() || "";

    if (filePath && content) {
      result.push({ [filePath]: content });
    }
  }

  return result;
};

const isValidTestFilesArray = (response) => {
  return (
    Array.isArray(response) &&
    response.length > 0 &&
    response.every(
      (item) =>
        typeof item === "object" &&
        Object.keys(item).length === 1 &&
        Object.keys(item)[0].includes("tests/specs/")
    )
  );
};

// Utility function to convert page classes string to array (for page objects)
const convertPageClassesStringToArray = (pageClassesString) => {
  if (!pageClassesString || typeof pageClassesString !== "string") {
    return [];
  }

  const fileSections = pageClassesString.split(/\/\/ src\/pages\/[^\n]+/);
  const fileComments =
    pageClassesString.match(/\/\/ src\/pages\/[^\n]+/g) || [];

  const result = [];
  for (let i = 0; i < fileComments.length; i++) {
    const filePath = fileComments[i].replace("// ", "");
    const content = fileSections[i + 1]?.trim() || "";

    if (filePath && content) {
      result.push({ [filePath]: content });
    }
  }

  return result;
};

const GenerateTestFilesPage = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [zipBlob, setZipBlob] = useState(null);
  const [generatedProject, setGeneratedProject] = useState(null);
  const [projectGenerationFailed, setProjectGenerationFailed] = useState(false);
  const [githubConfigOpen, setGithubConfigOpen] = useState(false);
  const [isPushingToGit, setIsPushingToGit] = useState(false);
  const [githubSuccessOpen, setGithubSuccessOpen] = useState(false);
  const [githubSuccessData, setGithubSuccessData] = useState(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasPushedToGit, setHasPushedToGit] = useState(false);
  const { setLoadingState } = useContext(LoadingModalContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { 
    automationType, 
    preferences, 
    environment, 
    currentStep, 
    stateStepIndex,
    testCases,
    currentTestCaseIndex,
    generatedLocators,
    generatedPageObjects,
    generatedTestFiles,
    pageSource,
    projectId,
    featureFileContent,
  } = location.state || {};

  // Initialize edited content
  React.useEffect(() => {
    // Debug logging
    console.log(
      "GenerateTestFilesPage - generatedTestFiles:",
      generatedTestFiles
    );
    console.log(
      "GenerateTestFilesPage - generatedTestFiles type:",
      typeof generatedTestFiles
    );

    // Check if generatedTestFiles is a valid JSON array format
    if (generatedTestFiles && isValidTestFilesArray(generatedTestFiles)) {
      // Convert JSON array to string format for display
      const contentToUse = convertTestFilesArrayToString(generatedTestFiles);
      setEditedContent(contentToUse);
      console.log(
        `✅ Loaded ${generatedTestFiles.length} test files from JSON array format`
      );
    } else {
      // Handle legacy string format or no data
      const contentToUse =
        generatedTestFiles ||
        "// No test files generated yet. Please generate test files first.";
    setEditedContent(contentToUse);
      console.log("📝 Using legacy string format or no data");
    }
  }, [generatedTestFiles]);

  const getStepPath = (stepName) => {
    const stepPaths = {
      "Test Cases":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
      "Enter Page DOM":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut",
      "Generate Locators":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      "Generate Page Objects":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects",
      "Generate Test Files":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files",
    };
    return (
      stepPaths[stepName] ||
      "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files"
    );
  };

  const handleBack = () => {
    const steps = getStepperSteps(preferences);
    const currentIndex = getStepIndex(steps, "Generate Test Files");
    const previousStep = getPreviousStep(steps, currentIndex);
    const previousPath = getStepPath(previousStep);
    navigate(previousPath, {
      state: {
        automationType,
        preferences,
        environment,
        currentStep: previousStep,
        stateStepIndex: getStepIndex(steps, previousStep),
        testCases,
        currentTestCaseIndex,
        generatedLocators,
        pageSource,
        featureFileContent,
      },
    });
  };

  const handleNext = async () => {
    let fake;
    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 2.4;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        setLoadingState((prev) => ({
          ...prev,
          progress: Math.min(progress, 98),
        }));
      }, 100);

      return {
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          setLoadingState((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((resolve) => setTimeout(resolve, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };

    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Project",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      // Convert edited test files string content back to JSON array format for API call
      const testFilesArray = convertTestFilesStringToArray(editedContent);

      // Debug logging for page objects format
      console.log("🔍 Page Objects format check:");
      console.log("🔍 generatedPageObjects type:", typeof generatedPageObjects);
      console.log(
        "🔍 generatedPageObjects is array:",
        Array.isArray(generatedPageObjects)
      );
      console.log(
        "🔍 generatedPageObjects length:",
        Array.isArray(generatedPageObjects)
          ? generatedPageObjects.length
          : "N/A"
      );
      console.log("🔍 generatedPageObjects sample:", generatedPageObjects);

      // Ensure page objects are in the correct format
      let pageObjectsToSend = generatedPageObjects;
      if (typeof generatedPageObjects === "string") {
        // If it's a string, try to convert it to array format
        console.log(
          "⚠️ Page objects are in string format, attempting conversion..."
        );
        pageObjectsToSend =
          convertPageClassesStringToArray(generatedPageObjects);
        console.log(
          "✅ Converted page objects to array:",
          pageObjectsToSend.length,
          "items"
        );
      } else if (!Array.isArray(generatedPageObjects)) {
        console.log(
          "⚠️ Page objects are not in expected format, using empty array"
        );
        pageObjectsToSend = [];
      }

      // Prepare data for project generation
      const requestData = {
        testFiles: testFilesArray, // Send as JSON array
        pageObjects: pageObjectsToSend, // Ensure it's an array
        locators: generatedLocators,
        testCases: testCases,
        currentTestCaseIndex: currentTestCaseIndex,
        pageSource: pageSource,
        automationType: automationType,
        preferences: preferences,
        environment: environment,
        projectId: projectId, // Include project ID for test data association
      };

      console.log(
        `📤 Sending ${testFilesArray.length} test files and ${pageObjectsToSend.length} page objects to project API`
      );
      console.log(`🆔 Project ID being sent:`, projectId);

      // Call the Playwright TypeScript Project API
      const response = await playwrightTypeScriptProjectAPI(requestData);
      const generatedProject = response.data;
      
      // Debug logging
      console.log("🔍 Project generation response:", response);
      console.log("🔍 Response status:", response.status);
      console.log("🔍 Response headers:", response.headers);
      console.log("🔍 Generated project data:", generatedProject);
      console.log("🔍 Generated project type:", typeof generatedProject);
      console.log(
        "🔍 Generated project is array:",
        Array.isArray(generatedProject)
      );
      console.log(
        "🔍 Generated project constructor:",
        generatedProject?.constructor?.name
      );
      if (Array.isArray(generatedProject)) {
        console.log("✅ Generated project length:", generatedProject.length);
        console.log("✅ First few items:", generatedProject.slice(0, 3));
      } else {
        console.log("❌ Generated project is not an array");
        console.log(
          "❌ Generated project value:",
          JSON.stringify(generatedProject, null, 2)
        );
      }

      await finishTo100AndClose();

      // Store the generated project and show success drawer
      setGeneratedProject(generatedProject);
      setProjectGenerationFailed(false); // Reset error state on success
      setSuccessPopupOpen(true);
    } catch (error) {
      console.error("Error generating project:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        error?.response?.data?.message ||
        "An error occurred while generating the project. Please try again.";
      
      // Set error state and show error message
      setProjectGenerationFailed(true);
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });
      
      // Show success drawer immediately with error state
      setSuccessPopupOpen(true);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(editedContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      setPopupMessage("Failed to copy to clipboard");
      setPopupOpen(true);
    }
  };

  const handleDownloadFile = () => {
    const blob = new Blob([editedContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "TestFile.ts";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Debug logging
      console.log(
        "GenerateTestFilesPage - generatedProject:",
        generatedProject
      );
      console.log(
        "GenerateTestFilesPage - generatedProject type:",
        typeof generatedProject
      );
      console.log(
        "GenerateTestFilesPage - generatedProject is array:",
        Array.isArray(generatedProject)
      );
      console.log(
        "GenerateTestFilesPage - generatedProject length:",
        generatedProject?.length
      );
      
      // Use the generated project data directly (should be in array format from backend)
      if (generatedProject && Array.isArray(generatedProject)) {
        console.log(
          "✅ Using generated project data with",
          generatedProject.length,
          "files"
        );
        console.log("First few files:", generatedProject.slice(0, 3));
        downloadPlaywrightTypeScriptProject(generatedProject, setZipBlob);
        setHasDownloaded(true); // Mark as downloaded
      } else {
        console.log(
          "❌ Using fallback project data - generatedProject is not an array"
        );
        console.log(
          "generatedProject value:",
          JSON.stringify(generatedProject, null, 2)
        );
        // Fallback: create a basic project structure if data is not in expected format
        const fallbackProject = [
          {
            "package.json":
              '{"name": "playwright-typescript-project", "version": "1.0.0", "scripts": {"test": "playwright test"}}',
          },
          {
            "playwright.config.ts":
              "import { defineConfig } from '@playwright/test'; export default defineConfig({});",
          },
          {
            "tsconfig.json":
              '{"compilerOptions": {"target": "ES2020", "module": "commonjs"}}',
          },
          {
            "README.md":
              "# Playwright TypeScript Project\n\nThis is a generated Playwright TypeScript test automation project.",
          },
        ];
        downloadPlaywrightTypeScriptProject(fallbackProject, setZipBlob);
        setHasDownloaded(true); // Mark as downloaded
      }
    } catch (error) {
      console.error("Error downloading project:", error);
      setPopupMessage("Error downloading project. Please try again.");
      setPopupOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePushToGitHub = () => {
    setGithubConfigOpen(true);
  };

  const handleGitHubConfigClose = () => {
    setGithubConfigOpen(false);
  };

  const handleGitHubSuccessClose = () => {
    setGithubSuccessOpen(false);
    setGithubSuccessData(null);
  };

  const handleGitHubConfigConfirm = async (config) => {
    setIsPushingToGit(true);
    
    // Start progress tracking
    setLoadingState({
      open: true,
      message: "Uploading Playwright TypeScript Project to GitHub",
      progress: 0,
    });

    let fake;
    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 0.001;
        if (progress >= 97 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 97) progress = 97;

        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);

          // Rush to 100%
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };

    fake = startProgress(setLoadingState);

    try {
      console.log("GitHub config:", config);
      console.log("Generated project:", generatedProject);
      
      // Create ZIP file from generated project using existing download function
      const zipBlob = await createZipFromProject(generatedProject);
      
      // Prepare FormData for the API call
      const formData = new FormData();
      formData.append('username', config.username);
      formData.append('token', config.token);
      formData.append('repoName', config.repoName);
      formData.append('zipfile', zipBlob, 'playwright-typescript-project.zip');

      console.log("Calling GitHub API with ZIP file...");
      
      // Call the actual GitHub push API
      const response = await updateGitHubProjectPlaywrightTypeScript(formData);
      
      console.log("GitHub API response:", response.data);
      
      if (response.data && response.data.pullRequestNumber) {
        console.log("Full GitHub response:", response.data);
        
        // Enhance the PR details with repository name from form
        const enhancedPrDetails = {
          ...response.data,
          repository: response.data.repository || `${config.username}/${config.repoName}`
        };
        
        console.log("PR Number:", enhancedPrDetails.pullRequestNumber);
        console.log("PR URL:", enhancedPrDetails.pullRequestUrl);
        console.log("Repository:", enhancedPrDetails.repository);
        
        await finishTo100AndClose();
        
        // Set success data and show success drawer
        setGithubSuccessData({
          repository: enhancedPrDetails.repository,
          pullRequestNumber: enhancedPrDetails.pullRequestNumber,
          pullRequestUrl: enhancedPrDetails.pullRequestUrl
        });
        setGithubSuccessOpen(true);
        setGithubConfigOpen(false);
        setHasPushedToGit(true); // Mark as pushed to GitHub
      } else {
        throw new Error("Failed to create pull request");
      }
    } catch (error) {
      console.error("Error uploading project to GitHub:", error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.Error || 
                          error?.message || 
                          "An error occurred while uploading the project to GitHub.";
      
      // Check if it's a repository name conflict
      const isRepoNameConflict = errorMessage.toLowerCase().includes('repository') && 
                                (errorMessage.toLowerCase().includes('exists') || 
                                 errorMessage.toLowerCase().includes('already') ||
                                 errorMessage.toLowerCase().includes('name'));
      
      if (isRepoNameConflict) {
        // For repository name conflicts, show a user-friendly message
        setPopupMessage(`Repository name "${config.repoName}" already exists. Please choose a different name.`);
      } else {
        // For other errors, show the actual error message
        setPopupMessage(errorMessage);
      }
      
    setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setIsPushingToGit(false);
    }
  };

  // Helper function to create ZIP from project data using existing download logic
  const createZipFromProject = async (projectData) => {
    return new Promise((resolve, reject) => {
      try {
        // Use the existing download function logic to create ZIP
        import('jszip').then(JSZip => {
          const zip = new JSZip.default();
          
          // Add all files from the generated project using the same logic as downloadPlaywrightTypeScriptProject
          if (projectData && Array.isArray(projectData)) {
            projectData.forEach(fileObj => {
              // Each fileObj is like: { "fileName": "content" }
              const fileName = Object.keys(fileObj)[0];
              const content = fileObj[fileName];
              
              if (fileName && content) {
                // Use the same file organization logic as the download function
                if (fileName === "package.json") {
                  zip.file("package.json", content);
                } else if (fileName === "playwright.config.ts") {
                  zip.file("playwright.config.ts", content);
                } else if (fileName === "tsconfig.json") {
                  zip.file("tsconfig.json", content);
                } else if (fileName === "README.md") {
                  zip.file("README.md", content);
                } else if (fileName.startsWith("src/pages/")) {
                  const pagesFolder = zip.folder("src/pages");
                  const relativePath = fileName.replace("src/pages/", "");
                  pagesFolder.file(relativePath, content);
                } else if (fileName.startsWith("tests/specs/")) {
                  const testsFolder = zip.folder("tests/specs");
                  const relativePath = fileName.replace("tests/specs/", "");
                  testsFolder.file(relativePath, content);
                } else if (fileName.startsWith("src/utils/")) {
                  const utilsFolder = zip.folder("src/utils");
                  const relativePath = fileName.replace("src/utils/", "");
                  utilsFolder.file(relativePath, content);
                } else if (fileName.startsWith("config/env/")) {
                  const configEnvFolder = zip.folder("config/env");
                  const relativePath = fileName.replace("config/env/", "");
                  configEnvFolder.file(relativePath, content);
                } else if (fileName === "config/env.ts") {
                  const configFolder = zip.folder("config");
                  configFolder.file("env.ts", content);
                } else if (fileName.startsWith("test-data/")) {
                  const testDataFolder = zip.folder("test-data");
                  const relativePath = fileName.replace("test-data/", "");
                  testDataFolder.file(relativePath, content);
                } else {
                  // Default: add file to root
                  zip.file(fileName, content);
                }
              }
            });
          }
          
          // Generate ZIP blob
          zip.generateAsync({ type: 'blob' }).then(blob => {
            resolve(blob);
          }).catch(reject);
        }).catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleSuccessPopupClose = () => {
    // Check if user has downloaded or pushed to GitHub
    if (!hasDownloaded && !hasPushedToGit && !projectGenerationFailed) {
      // Show warning popup if user tries to close without downloading or pushing
      setPopupMessage("Please download the project or push to GitHub before closing this window.");
      setPopupOpen(true);
      return; // Don't close the drawer
    }
    
    // Only close if user has downloaded, pushed, or there was an error
    setSuccessPopupOpen(false);
  };

  const handleErrorPopupClose = () => {
    setPopupOpen(false);
    // Success drawer is already open with error state, no need to open it again
  };

  // Memoize progressSteps to prevent re-render loops
  const progressSteps = useMemo(() => {
    const steps = getStepperSteps(preferences);
    console.log("Debug - progressSteps calculated:", steps);
    return steps;
  }, [preferences]);

  // Generate Test Files is always the 5th step (index 4) in the 5-step process
  // This should show as "Step 5/5" in the UI
  const currentStepIndex = 4; // Hardcoded to ensure correct step display
  
  // Debug logging removed to prevent console spam during re-renders

  return (
    <>
      {/* Success Popup */}
      <PlaywrightTypeScriptSuccessPopup
        open={successPopupOpen}
        handleClose={handleSuccessPopupClose}
        onDownload={handleDownload}
        onPushToGitHub={handlePushToGitHub}
        isDownloading={isDownloading}
        projectGenerationFailed={projectGenerationFailed}
      />

      {/* Error Message Popup */}
      <MessagePopup
        open={popupOpen}
        onClose={handleErrorPopupClose}
        message={popupMessage}
      />

      {/* GitHub Configuration Drawer */}
      <GitHubConfigDrawer
        open={githubConfigOpen}
        onClose={handleGitHubConfigClose}
        onConfirm={handleGitHubConfigConfirm}
        isLoading={isPushingToGit}
      />

      {/* GitHub Success Drawer */}
      <GitHubSuccessDrawer
        open={githubSuccessOpen}
        onClose={handleGitHubSuccessClose}
        repository={githubSuccessData?.repository}
        pullRequestNumber={githubSuccessData?.pullRequestNumber}
        pullRequestUrl={githubSuccessData?.pullRequestUrl}
      />

      <Box
        sx={{
        minHeight: "100vh", 
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
          py: 4,
        }}
      >
      <Container maxWidth="lg" sx={{ width: "100%", flex: 1 }}>
        {/* Custom Stepper */}
        <Box sx={{ mb: 4 }}>
          <CustomStepper
            steps={progressSteps}
            activeStep={currentStepIndex}
            maxWidth="100%"
            containerWidth="100%"
            activeColor="#1976d2"
            completedColor="#40a9ff"
            pendingColor="#d1d1d1"
            showNextStep={true}
            showStepCount={true}
            title="Now"
            inProgressText="(In Progress)"
            completedText="(Completed)"
            nextText="Next"
            completedNextText="Completed"
            stepText="Step"
          />
        </Box>

        {/* Page Title */}
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
            mb: { xs: 2, sm: 3 }, 
          fontWeight: "bold",
          color: "#333",
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
            fontFamily: "Manrope",
              textAlign: "left",
              lineHeight: { xs: 1.3, sm: 1.4 },
        }}
      >
          Test Files
      </Typography>

        {/* Main Content Card */}
          <Card
            sx={{
          maxWidth: "100%",
          borderRadius: { xs: "4px", sm: "8px" },
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          mb: { xs: 2, sm: 3, md: 4 },
              bgcolor: "white",
              overflow: "hidden",
            }}
          >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Click to edit label */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#666", 
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  cursor: isEditing ? "default" : "pointer",
                  "&:hover": {
                      color: isEditing ? "#666" : "#1976d2",
                    },
                }}
                onClick={!isEditing ? handleEditClick : undefined}
              >
                {isEditing ? "Editing..." : "Click to edit"}
      </Typography>

              <Tooltip title={isCopied ? "Copied!" : "Copy to clipboard"}>
                <Button
                  onClick={handleCopyToClipboard}
                  startIcon={<CopyIcon />}
                  sx={{
                    color: "#1976d2",
                    fontSize: "14px",
                    textTransform: "none",
                    minWidth: "auto",
                      p: 1,
                  }}
                >
                  Copy to clipboard
                </Button>
              </Tooltip>
            </Box>

            {/* Code Editor Box */}
            <Box
              sx={{
                width: "100%",
                bgcolor: "#000000",
                borderRadius: "8px",
                p: 0,
                border: "1px solid #d1d1d1",
                minHeight: "400px",
                  position: "relative",
              }}
            >
              {/* Edit mode buttons */}
              {isEditing && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: "flex",
                      gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleSaveEdit}
                    sx={{
                      color: "#4caf50",
                      fontSize: "12px",
                      textTransform: "none",
                      minWidth: "auto",
                        p: 1,
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCancelEdit}
                    sx={{
                      color: "#f44336",
                      fontSize: "12px",
                      textTransform: "none",
                      minWidth: "auto",
                        p: 1,
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}

              {/* Code Editor */}
              {isEditing ? (
                <Editor
                  height="400px"
                    defaultLanguage="typescript"
                  value={editedContent}
                    onChange={(value) => setEditedContent(value || "")}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                      fontFamily: "monospace",
                      lineNumbers: "on",
                      wordWrap: "on",
                    automaticLayout: true,
                    padding: { top: 16, bottom: 16 },
                      cursorStyle: "line",
                    selectOnLineNumbers: true,
                    roundedSelection: false,
                    readOnly: false,
                    contextmenu: true,
                    mouseWheelZoom: true,
                      cursorBlinking: "blink",
                    cursorSmoothCaretAnimation: true,
                      placeholder: "Enter test file content",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    padding: "16px",
                    color: "#ffffff",
                    fontFamily: "monospace",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    overflow: "auto",
                    cursor: "pointer",
                    minHeight: "400px",
                    display: "flex",
                      alignItems: "flex-start",
                  }}
                  onClick={handleEditClick}
                >
                  {editedContent || "Enter test file content"}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Footer Navigation */}
          <Box
            sx={{
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          mt: 4,
              px: 0,
            }}
          >
          {/* Left: Back Button */}
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{
                width: 120,
                height: 40,
                border: "2px solid #0094FB",
                borderRadius: 2,
                backgroundColor: "transparent",
                padding: "8px 16px",
                fontFamily: "Manrope",
                fontWeight: 700,
                fontSize: 20,
                lineHeight: "24px",
                letterSpacing: 0.15,
              color: "#0094FB",
              textTransform: "none",
              "&:hover": {
                  border: "2px solid #0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
                transition: "all 0.3s ease-out",
            }}
          >
            Back
          </Button>

          {/* Right: Download and Generate Project Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadFile}
              sx={{
                  width: 200,
                  height: 40,
                  border: "2px solid #0094FB",
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  padding: "8px 16px",
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "24px",
                  letterSpacing: 0.15,
                color: "#0094FB",
                textTransform: "none",
                "&:hover": {
                    border: "2px solid #0094FB",
                    backgroundColor: "rgba(0, 148, 251, 0.04)",
                  },
                  transition: "all 0.3s ease-out",
              }}
            >
              Download File
            </Button>

            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              sx={{
                  width: 230,
                  height: 40,
                backgroundColor: "#0094FB",
                  borderRadius: 2,
                  padding: "8px 16px",
                  fontFamily: "Manrope",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: "24px",
                  letterSpacing: 0.15,
                color: "white",
                textTransform: "none",
                  border: "none",
                "&:hover": {
                    backgroundColor: "#007ACC",
                  },
                  transition: "all 0.3s ease-out",
              }}
            >
              Generate Project
            </Button>
          </Box>
        </Box>
      </Container>
      </Box>
    </>
  );
};

export default GenerateTestFilesPage;
